import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Pagination, Stack, Typography, Chip, Button, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Avatar, Grid, Card, CardContent, TextField, MenuItem, Alert, InputAdornment, TableSortLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { fetchAllEvents, fetchTopParticipantsByAttendanceRate, fetchAllParticipantStats, recalculateAllParticipantStats, searchClubEvents } from '../../controllers/clubEventsController';
import CreateClubEventDialog from './CreateClubEventDialog';
import ClubEventsCalendar from './ClubEventsCalendar';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { deleteEvent as deleteEventAction, fetchEventById } from '../../controllers/clubEventsController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditClubEventDialog from './EditClubEventDialog';
import { formatTurkeyLocalIntl } from '../../utils/turkeyLocalDateTime';
import { resolveGalleryAssetUrl } from '../../api/httpClientGalleries';

const tableHeadCellSx = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
};

const sortEvents = (list, field, direction) => {
  const dir = direction === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (field) {
      case 'title':
        return dir * (a.title || '').localeCompare(b.title || '', 'tr', { sensitivity: 'base' });
      case 'startDate': {
        const aTime = a.startDate ? new Date(a.startDate).getTime() : 0;
        const bTime = b.startDate ? new Date(b.startDate).getTime() : 0;
        return dir * (aTime - bTime);
      }
      case 'city':
        return dir * (a.city || '').localeCompare(b.city || '', 'tr', { sensitivity: 'base' });
      case 'status': {
        const statusScore = (ev) => {
          let score = 0;
          if (ev.isActive) score += 2;
          if (ev.isCompleted) score += 1;
          return score;
        };
        return dir * (statusScore(a) - statusScore(b));
      }
      case 'participantCount':
        return dir * ((a.participantCount ?? 0) - (b.participantCount ?? 0));
      default:
        return 0;
    }
  });
};

const EventsTableHeadRow = ({ sortField, sortDirection, onSort }) => {
  const renderSortableCell = (field, label, align) => {
    const isActive = sortField === field;
    return (
      <TableCell
        align={align}
        sx={tableHeadCellSx}
        sortDirection={isActive ? sortDirection : false}
      >
        <TableSortLabel
          active={isActive}
          direction={isActive ? sortDirection : 'asc'}
          onClick={() => onSort(field)}
          sx={{
            color: isActive ? '#0E4D94' : '#a3a3a3',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              color: isActive ? '#0E4D94' : '#666666',
            },
            '&.Mui-active': {
              color: '#0E4D94',
            },
            '& .MuiTableSortLabel-icon': {
              opacity: '1 !important',
              color: isActive ? '#0E4D94 !important' : '#a3a3a3 !important',
            },
            '&:hover .MuiTableSortLabel-icon': {
              opacity: '1 !important',
              color: isActive ? '#0E4D94 !important' : '#666666 !important',
            },
          }}
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <TableRow>
      {renderSortableCell('title', 'Başlık')}
      {renderSortableCell('startDate', 'Tarih')}
      {renderSortableCell('city', 'Şehir')}
      {renderSortableCell('status', 'Durum')}
      {renderSortableCell('participantCount', 'Katılımcı')}
      <TableCell align="right" sx={tableHeadCellSx}>İşlemler</TableCell>
    </TableRow>
  );
};

const ClubEventsList = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('startDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [createSuccessMessage, setCreateSuccessMessage] = useState('');
  const [createWarningMessage, setCreateWarningMessage] = useState('');
  const [tab, setTab] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedEventData, setSelectedEventData] = useState(null);
  const [topParticipants, setTopParticipants] = useState([]);
  const [loadingTopParticipants, setLoadingTopParticipants] = useState(false);
  const [topParticipantsError, setTopParticipantsError] = useState('');
  
  // Participant Stats states
  const [statsItems, setStatsItems] = useState([]);
  const [statsPage, setStatsPage] = useState(1);
  const [statsPageSize] = useState(20);
  const [statsTotalPages, setStatsTotalPages] = useState(1);
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [statsFilters, setStatsFilters] = useState({
    type: '',
    minAttendanceRate: '',
    maxAttendanceRate: '',
    minLifetimeAttendanceRate: '',
    maxLifetimeAttendanceRate: '',
    minAttendedEventsCount: '',
    minRegisteredEventsCount: '',
    searchKeyword: '',
    sortBy: 0, // ParticipantStatsSortBy.AttendanceRateDesc
  });
  const [recalculatingAll, setRecalculatingAll] = useState(false);
  const [recalculateAllDialogOpen, setRecalculateAllDialogOpen] = useState(false);

  const loadEvents = async (targetPage = page, keyword = debouncedSearch) => {
    const trimmed = keyword.trim();
    if (trimmed) {
      return searchClubEvents({ keyword: trimmed, pageNumber: targetPage, pageSize });
    }
    return fetchAllEvents({ pageNumber: targetPage, pageSize });
  };

  const sortedItems = useMemo(
    () => sortEvents(items, sortField, sortDirection),
    [items, sortField, sortDirection],
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (tab !== 0) return;
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data, meta } = await loadEvents(page, debouncedSearch);
        if (!active) return;
        setItems(data || []);
        setTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        setError(e?.message || 'Etkinlikler alınamadı');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, pageSize, debouncedSearch, tab]);

  // Fetch top participants when tab 2 is selected
  useEffect(() => {
    if (tab !== 2) return;
    let active = true;
    (async () => {
      setLoadingTopParticipants(true);
      setTopParticipantsError('');
      try {
        const data = await fetchTopParticipantsByAttendanceRate(20);
        if (!active) return;
        setTopParticipants(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!active) return;
        setTopParticipantsError(e?.message || 'En yüksek katılım oranına sahip katılımcılar alınamadı');
        setTopParticipants([]);
      } finally {
        if (active) setLoadingTopParticipants(false);
      }
    })();
    return () => { active = false; };
  }, [tab]);

  // Fetch participant stats when tab 3 is selected
  useEffect(() => {
    if (tab !== 3) return;
    let active = true;
    (async () => {
      setLoadingStats(true);
      setStatsError('');
      try {
        const params = {
          pageNumber: statsPage,
          pageSize: statsPageSize,
          sortBy: statsFilters.sortBy,
        };
        if (statsFilters.type !== '') params.type = Number(statsFilters.type);
        if (statsFilters.minAttendanceRate !== '') params.minAttendanceRate = Number(statsFilters.minAttendanceRate);
        if (statsFilters.maxAttendanceRate !== '') params.maxAttendanceRate = Number(statsFilters.maxAttendanceRate);
        if (statsFilters.minLifetimeAttendanceRate !== '') params.minLifetimeAttendanceRate = Number(statsFilters.minLifetimeAttendanceRate);
        if (statsFilters.maxLifetimeAttendanceRate !== '') params.maxLifetimeAttendanceRate = Number(statsFilters.maxLifetimeAttendanceRate);
        if (statsFilters.minAttendedEventsCount !== '') params.minAttendedEventsCount = Number(statsFilters.minAttendedEventsCount);
        if (statsFilters.minRegisteredEventsCount !== '') params.minRegisteredEventsCount = Number(statsFilters.minRegisteredEventsCount);
        if (statsFilters.searchKeyword.trim()) params.searchKeyword = statsFilters.searchKeyword.trim();
        
        const { data, meta } = await fetchAllParticipantStats(params);
        if (!active) return;
        setStatsItems(Array.isArray(data) ? data : []);
        setStatsTotalPages(meta?.totalPages || 1);
      } catch (e) {
        if (!active) return;
        setStatsError(e?.message || 'Katılımcı istatistikleri alınamadı');
        setStatsItems([]);
      } finally {
        if (active) setLoadingStats(false);
      }
    })();
    return () => { active = false; };
  }, [tab, statsPage, statsPageSize, statsFilters]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Etkinlikler</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ width: 16 }} />}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E7EDF4',
            boxShadow: 'none',
            fontSize: 13,
            borderRadius: '5px',
            color: '#0E4D94',
            '&:hover': {
              backgroundColor: '#CFDBEA',
              boxShadow: 'none',
            },
          }}
          onClick={() => setCreateOpen(true)}
        >
          Yeni Etkinlik
        </Button>
      </Stack>
      {createSuccessMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreateSuccessMessage('')}>
          {createSuccessMessage}
        </Alert>
      )}
      {createWarningMessage && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCreateWarningMessage('')}>
          {createWarningMessage}
        </Alert>
      )}
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#a3a3a3',
            '&.Mui-selected': {
              color: '#0E4D94',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#0E4D94',
          },
        }}
      >
            <Tab label="Liste" />
            <Tab label="Takvim" />
            {/* <Tab label="En Yüksek Katılım" /> */}
            {/* <Tab label="İstatistikler" /> */}
          </Tabs>
      {tab === 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Etkinlik adı ile ara..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch('');
                        setPage(1);
                      }}
                      sx={{ p: 0.5 }}
                    >
                      <ClearIcon sx={{ fontSize: 18, color: '#666' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                maxWidth: 420,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '14px',
                  '& fieldset': { borderColor: '#e6e6e6' },
                  '&:hover fieldset': { borderColor: '#0E4D94' },
                  '&.Mui-focused fieldset': { borderColor: '#0E4D94' },
                },
              }}
            />
          </Box>
          {loading ? (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5
              }}
            >
        <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <EventsTableHeadRow
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableBody>
                  {Array.from({ length: pageSize }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: index === pageSize - 1 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell>
                        <Skeleton width={200} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={250} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={100} height={20} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={80} height={24} borderRadius={16} />
                      </TableCell>
                      <TableCell>
                        <Skeleton width={60} height={20} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Skeleton width={32} height={32} borderRadius={6} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : error ? (
            <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
          ) : items.length === 0 ? (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5
              }}
            >
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <EventsTableHeadRow
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: '#a3a3a3' }}>
                        {debouncedSearch ? 'Arama kriterlerine uygun etkinlik bulunamadı.' : 'Henüz etkinlik eklenmemiş'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                border: '1px solid #f5f5f5',
                py: 1,
                px: 2.5
              }}
            >
        <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <EventsTableHeadRow
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
          <TableBody>
                  {sortedItems.map((ev, index) => (
                    <TableRow 
                      key={ev.id} 
                      hover 
                      sx={{ 
                        cursor: 'pointer',
                        '& .MuiTableCell-root': {
                          borderBottom: index === sortedItems.length - 1 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                          color: '#444444',
                          fontWeight: 500,
                        },
                        '&:hover': {
                          backgroundColor: '#fafafa',
                        },
                      }} 
                      onClick={() => navigate(`/events/${ev.id}`)}
                    >
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    {(ev.coverImageUrl || ev.CoverImageUrl) && (
                      <Box
                        component="img"
                        src={resolveGalleryAssetUrl(ev.coverImageUrl || ev.CoverImageUrl)}
                        alt=""
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #e6e6e6',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span>{ev.title}</span>
                  </Stack>
                </TableCell>
                <TableCell>
                        {formatTurkeyLocalIntl(ev.startDate)} - {formatTurkeyLocalIntl(ev.endDate)}
                </TableCell>
                      <TableCell>{ev.city || '-'}</TableCell>
                <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {!ev.isActive && (
                            <Chip 
                              label="Pasif" 
                              size="small" 
                              sx={{
                                backgroundColor: '#FFF4E6',
                                color: '#FF9800',
                                fontSize: '11px',
                                fontWeight: 600,
                                height: '22px',
                                borderRadius: '12px',
                              }}
                            />
                          )}
                          {ev.isCompleted && (
                            <Chip 
                              label="Tamamlandı" 
                              size="small" 
                              sx={{
                                backgroundColor: '#DFFFEA',
                                color: '#17C653',
                                fontSize: '11px',
                                fontWeight: 600,
                                height: '22px',
                                borderRadius: '12px',
                              }}
                            />
                          )}
                  </Stack>
                </TableCell>
                      <TableCell>{typeof ev.participantCount === 'number' ? ev.participantCount : '-'}</TableCell>
                <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="Düzenle">
                            <IconButton 
                              size="small" 
                              onClick={async () => {
                                try {
                                  const eventData = await fetchEventById(ev.id);
                                  setSelectedEventData(eventData);
                                  setEditOpen(true);
                                } catch (err) {
                                  alert(err?.message || 'Etkinlik bilgileri alınamadı');
                                }
                              }}
                              sx={{
                                backgroundColor: '#E7EDF4',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#CFDBEA',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                  color: '#0E4D94',
                                },
                              }}
                            >
                              <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                            <IconButton 
                              size="small" 
                              onClick={() => { setSelected(ev); setConfirmOpen(true); }}
                              sx={{
                                backgroundColor: '#FFEEF3',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#FFD6E0',
                                },
                                '& svg': {
                                  fontSize: '14px',
                                  color: '#F8285A',
                                },
                              }}
                            >
                              <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                        </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
          )}
        </>
      )}
      {tab === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} />
        </Box>
      )}
      {tab === 1 && (
        <Paper sx={{ p: 1 }}>
          <ClubEventsCalendar />
        </Paper>
      )}
      {tab === 2 && (
        <Box>
          {loadingTopParticipants ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : topParticipantsError ? (
            <Typography color="error" sx={{ mt: 2 }}>{topParticipantsError}</Typography>
          ) : topParticipants.length === 0 ? (
            <Box
              sx={{
                py: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                border: '2px dashed #e6e6e6',
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                Henüz katılımcı istatistiği bulunmamaktadır.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              {topParticipants.map((participant, index) => (
                  <Card 
                  key={participant.memberId || index} 
                    sx={{ 
                      p: 2.5,
                      border: '1px solid #e7e7e7',
                      boxShadow: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      '&:hover': {
                        borderColor: '#0E4D94',
                        boxShadow: '0px 2px 8px rgba(14, 77, 148, 0.1)',
                      },
                    }}
                    onClick={() => navigate(`/members/${participant.memberId}`)}
                  >
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box sx={{ position: 'relative' }}>
                          <Avatar 
                            src={resolveGroupsAssetUrl(participant.profilePictureUrl)} 
                            sx={{ width: 48, height: 48, border: '2px solid #e6e6e6' }}
                          />
                          {index < 3 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                backgroundColor: '#0E4D94',
                                color: '#ffffff',
                                borderRadius: '50%',
                                width: 24,
                                height: 24,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 700,
                                border: '2px solid #ffffff',
                              }}
                            >
                              {index + 1}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '14px',
                              fontWeight: 600,
                              color: '#444444',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              mb: 0.5,
                            }}
                          >
                            {participant.firstName} {participant.lastName}
                          </Typography>
                          <Chip 
                            label={participant.type === 0 ? 'Üye' : 'Yakın'} 
                            size="small" 
                            sx={{
                              height: 24,
                              fontSize: '11px',
                              fontWeight: 500,
                              backgroundColor: participant.type === 0 ? '#E7EDF4' : '#FFF4E6',
                              color: participant.type === 0 ? '#0E4D94' : '#FF9800',
                              border: 'none',
                            }}
                          />
                        </Box>
                      </Stack>
                      <Box sx={{ pt: 1.5, borderTop: '1px solid #e6e6e6' }}>
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: '24px',
                              fontWeight: 600,
                              color: '#0E4D94',
                            }}
                          >
                            {Number(participant.attendanceRate).toFixed(1)}%
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontSize: '13px',
                              color: '#666666',
                              fontWeight: 500,
                            }}
                          >
                            Katılım Oranı
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </Card>
              ))}
            </Box>
          )}
        </Box>
      )}
      {tab === 3 && (
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setRecalculateAllDialogOpen(true)}
              disabled={recalculatingAll}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                borderRadius: '5px',
                borderColor: '#e6e6e6',
                color: '#666666',
                '&:hover': {
                  borderColor: '#0E4D94',
                  backgroundColor: 'rgba(14, 77, 148, 0.04)',
                },
              }}
            >
              Tümünü Yeniden Hesapla
            </Button>
          </Stack>
          
          {/* Filters */}
          <Card sx={{ p: 2.5, border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px', mb: 2 }}>
            <Typography variant="body1" sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 2 }}>Filtreler</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Ara
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="İsim ara..."
                  value={statsFilters.searchKeyword}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, searchKeyword: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Tür
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  select
                  value={statsFilters.type}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, type: e.target.value }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="0">Üye</MenuItem>
                  <MenuItem value="1">Yakın</MenuItem>
                </TextField>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Min Katılım Oranı (%)
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={statsFilters.minAttendanceRate}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, minAttendanceRate: e.target.value }))}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Max Katılım Oranı (%)
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={statsFilters.maxAttendanceRate}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, maxAttendanceRate: e.target.value }))}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Min Yaşam Boyu Katılım (%)
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={statsFilters.minLifetimeAttendanceRate}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, minLifetimeAttendanceRate: e.target.value }))}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Min Katıldığı Etkinlik
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={statsFilters.minAttendedEventsCount}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, minAttendedEventsCount: e.target.value }))}
                  inputProps={{ min: 0 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Min Kayıtlı Etkinlik
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={statsFilters.minRegisteredEventsCount}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, minRegisteredEventsCount: e.target.value }))}
                  inputProps={{ min: 0 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Sıralama
                  </Typography>
                <TextField
                  fullWidth
                  size="small"
                  select
                  value={statsFilters.sortBy}
                  onChange={(e) => setStatsFilters((f) => ({ ...f, sortBy: Number(e.target.value) }))}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: '14px',
                        '& fieldset': {
                          borderColor: '#e6e6e6',
                        },
                        '&:hover fieldset': {
                          borderColor: '#0E4D94',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0E4D94',
                        },
                      },
                    }}
                >
                  <MenuItem value={0}>Katılım Oranı (Yüksekten Düşüğe)</MenuItem>
                  <MenuItem value={1}>Katılım Oranı (Düşükten Yükseğe)</MenuItem>
                  <MenuItem value={2}>Yaşam Boyu Katılım (Yüksekten Düşüğe)</MenuItem>
                  <MenuItem value={3}>Yaşam Boyu Katılım (Düşükten Yükseğe)</MenuItem>
                  <MenuItem value={4}>Katıldığı Etkinlik (Yüksekten Düşüğe)</MenuItem>
                  <MenuItem value={5}>Katıldığı Etkinlik (Düşükten Yükseğe)</MenuItem>
                  <MenuItem value={6}>Kayıtlı Etkinlik (Yüksekten Düşüğe)</MenuItem>
                  <MenuItem value={7}>Kayıtlı Etkinlik (Düşükten Yükseğe)</MenuItem>
                </TextField>
                </Box>
              </Grid>
            </Grid>
          </Card>

          {loadingStats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : statsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{statsError}</Alert>
          ) : statsItems.length === 0 ? (
            <Box
              sx={{
                py: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                backgroundColor: '#fafafa',
                border: '2px dashed #e6e6e6',
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                Katılımcı istatistiği bulunamadı.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer
                sx={{
                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f5f5f5',
                  py: 1,
                  px: 2.5,
                  borderRadius: '8px',
                }}
              >
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'transparent' }}>
                    <TableRow>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Foto</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ad Soyad</TableCell>
                      <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Tür</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kayıtlı</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katıldı</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Gelmedi</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İptal</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Beklemede</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katılım Oranı</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Yaşam Boyu Katılım</TableCell>
                      <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statsItems.map((stat, index) => (
                      <TableRow
                        key={stat.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          '& .MuiTableCell-root': {
                            borderBottom: index === statsItems.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            px: 1.5,
                            py: 1.5,
                            color: '#444444',
                            fontWeight: 500,
                            fontSize: '14px',
                          },
                          '&:hover': {
                            backgroundColor: '#fafafa',
                          },
                        }}
                        onClick={() => navigate(`/members/${stat.memberId}`)}
                      >
                        <TableCell>
                          <Avatar
                            src={resolveGroupsAssetUrl(stat.profilePictureUrl)}
                            sx={{ width: 32, height: 32 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#444444' }}>
                            {stat.firstName} {stat.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={stat.type === 0 ? 'Üye' : 'Yakın'}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '11px',
                              fontWeight: 500,
                              backgroundColor: stat.type === 0 ? '#E7EDF4' : '#FFF4E6',
                              color: stat.type === 0 ? '#0E4D94' : '#FF9800',
                              border: 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">{stat.registeredEventsCount ?? 0}</TableCell>
                        <TableCell align="right">{stat.attendedEventsCount ?? 0}</TableCell>
                        <TableCell align="right">{stat.absentEventsCount ?? 0}</TableCell>
                        <TableCell align="right">{stat.cancelledEventsCount ?? 0}</TableCell>
                        <TableCell align="right">{stat.pendingEventsCount ?? 0}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#0E4D94' }}>
                            {Number(stat.attendanceRate ?? 0).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600, color: '#17C653' }}>
                            {Number(stat.lifetimeAttendanceRate ?? 0).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Üye Detayı">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/members/${stat.memberId}`)}
                              sx={{
                                width: 28,
                                height: 28,
                                backgroundColor: '#E7EDF4',
                                color: '#0E4D94',
                                '&:hover': {
                                  backgroundColor: '#CFDBEA',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                },
                              }}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination
                  count={statsTotalPages}
                  page={statsPage}
                  onChange={(e, v) => setStatsPage(v)}
                />
              </Box>
            </>
          )}
        </Box>
      )}
      <CreateClubEventDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async ({ sendPushNotification } = {}) => {
          setCreateOpen(false);
          setCreateWarningMessage('');
          if (sendPushNotification) {
            setCreateSuccessMessage('Etkinlik oluşturuldu ve üyelere bildirim gönderildi.');
          } else {
            setCreateSuccessMessage('Etkinlik oluşturuldu.');
          }
          const { data, meta } = await loadEvents(page);
          setItems(data || []);
          setTotalPages(meta?.totalPages || 1);
        }}
      />
      <EditClubEventDialog
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setSelectedEventData(null);
        }}
        eventData={selectedEventData}
        onSaved={async () => {
          setEditOpen(false);
          setSelectedEventData(null);
          const { data, meta } = await loadEvents(page);
          setItems(data || []);
          setTotalPages(meta?.totalPages || 1);
        }}
      />
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Etkinliği Sil
          <IconButton
            onClick={() => setConfirmOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            {selected ? `"${selected.title}" etkinliğini silmek istediğinize emin misiniz?` : 'Seçili etkinliği silmek istediğinize emin misiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
            if (!selected) return;
            try {
              await deleteEventAction(selected.id);
              setConfirmOpen(false);
              setSelected(null);
              const { data, meta } = await loadEvents(page);
              setItems(data || []);
              setTotalPages(meta?.totalPages || 1);
            } catch (e) {}
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#F8285A',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#E01E4A',
              },
            }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Recalculate All Stats Dialog */}
      <Dialog open={recalculateAllDialogOpen} onClose={() => setRecalculateAllDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Tüm İstatistikleri Yeniden Hesapla
          <IconButton
            onClick={() => setRecalculateAllDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ fontSize: '14px', color: '#444444' }}>
            Tüm üye ve yakınların katılım istatistiklerini yeniden hesaplamak istediğinize emin misiniz? Bu işlem biraz zaman alabilir.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => setRecalculateAllDialogOpen(false)}
            disabled={recalculatingAll}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setRecalculatingAll(true);
              try {
                await recalculateAllParticipantStats();
                setRecalculateAllDialogOpen(false);
                // Refresh stats if on stats tab
                if (tab === 3) {
                  const params = {
                    pageNumber: statsPage,
                    pageSize: statsPageSize,
                    sortBy: statsFilters.sortBy,
                  };
                  if (statsFilters.type !== '') params.type = Number(statsFilters.type);
                  if (statsFilters.minAttendanceRate !== '') params.minAttendanceRate = Number(statsFilters.minAttendanceRate);
                  if (statsFilters.maxAttendanceRate !== '') params.maxAttendanceRate = Number(statsFilters.maxAttendanceRate);
                  if (statsFilters.minLifetimeAttendanceRate !== '') params.minLifetimeAttendanceRate = Number(statsFilters.minLifetimeAttendanceRate);
                  if (statsFilters.maxLifetimeAttendanceRate !== '') params.maxLifetimeAttendanceRate = Number(statsFilters.maxLifetimeAttendanceRate);
                  if (statsFilters.minAttendedEventsCount !== '') params.minAttendedEventsCount = Number(statsFilters.minAttendedEventsCount);
                  if (statsFilters.minRegisteredEventsCount !== '') params.minRegisteredEventsCount = Number(statsFilters.minRegisteredEventsCount);
                  if (statsFilters.searchKeyword.trim()) params.searchKeyword = statsFilters.searchKeyword.trim();
                  
                  const { data, meta } = await fetchAllParticipantStats(params);
                  setStatsItems(Array.isArray(data) ? data : []);
                  setStatsTotalPages(meta?.totalPages || 1);
                }
              } catch (e) {
                alert(e?.message || 'İstatistikler yeniden hesaplanamadı');
              } finally {
                setRecalculatingAll(false);
              }
            }}
            disabled={recalculatingAll}
            startIcon={recalculatingAll ? <CircularProgress size={16} /> : <RefreshIcon />}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': {
                backgroundColor: '#0a3d7a',
              },
              '&:disabled': {
                backgroundColor: '#e6e6e6',
                color: '#a3a3a3',
              },
            }}
          >
            {recalculatingAll ? 'Hesaplanıyor...' : 'Yeniden Hesapla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubEventsList;


